import React, { useEffect, useState } from 'react'
import {
  Card, Row, Col, Table, Button, Tag, Input, Badge, Space, Tooltip, Popconfirm, Divider, Tabs, Modal, Progress,
} from 'antd'
import {
  PlusOutlined, SearchOutlined, EditOutlined, EyeOutlined, DeleteOutlined,
  WarningOutlined, CheckCircleOutlined, ThunderboltOutlined, TeamOutlined,
  SendOutlined, CloseCircleOutlined,
} from '@ant-design/icons'
import { useNavigate } from 'react-router-dom'
import { useMasterData, type Generator, type Consumer } from '@/context/MasterDataContext'
import { useModulePermission } from '@/hooks/useModulePermission'
import { WorkflowChainDisplay } from '@/components/WorkflowChainDisplay'
import { mappingStore, GeneratorMapping, approvalOf, APPROVAL_LABEL, APPROVAL_PILL } from './mappingStore'
import {
  canApproveMappings, canSubmitForApproval, submitForApproval,
  approveMapping, rejectMapping,
} from './mappingApproval'
import ConsumerAllocationEditor from './ConsumerAllocationEditor'
import TableSkeleton from '@/components/TableSkeleton'
import KpiCardRow from '@/components/KpiCardRow'
import { formatIndianCompact, formatIndianFull, annualToMonthlyUnits, formatUnitsKWh } from '@/utils/numberFormat'

const plantTypeColor: Record<string, string> = {
  'Wind & Solar': '#2563EB',
  'Solar':        '#F59E0B',
  'Biomass':      '#059669',
  'Hydro':        '#0891B2',
}
const typeColor = (t?: string) => plantTypeColor[t || ''] || '#6366f1'

interface GenRow {
  gen: Generator
  groups: GeneratorMapping[]
  consumerCount: number
  status: 'draft' | 'active'
}

// One generator link for a consumer — a consumer can draw from multiple
// generators/plants, each with its own share, units and tariff.
interface ConsumerLink {
  mappingId: string
  generatorId: string
  generatorName: string
  plantName?: string
  htNumber?: string
  shareHolding: number
  allocatedUnits?: number
  tariff: number
  status: 'active' | 'suspended'
}

interface ConsumerRow {
  consumer: Consumer
  links: ConsumerLink[]
  totalAllocatedUnits: number
  status: 'draft' | 'active'
}

const MappingList: React.FC = () => {
  const navigate = useNavigate()
  const { generators, consumers } = useMasterData()
  const { canAdd, canEdit, canDelete } = useModulePermission('Mapping')
  const [search, setSearch] = useState('')
  const [consumerSearch, setConsumerSearch] = useState('')
  const [mappings, setMappings] = useState<GeneratorMapping[]>(() => mappingStore.getAll())

  // Which generator or consumer row is expanded, which group is being edited, and which unmapped plant is preselected.
  const [expandedGenId, setExpandedGenId] = useState<string | null>(null)
  const [expandedConsumerId, setExpandedConsumerId] = useState<string | null>(null)
  const [editingGroupId, setEditingGroupId] = useState<string | undefined>(undefined)
  const [selectedUnmappedPlantId, setSelectedUnmappedPlantId] = useState<string | undefined>(undefined)
  const [rejectTarget, setRejectTarget] = useState<string | null>(null)
  const [rejectReason, setRejectReason] = useState('')
  const { roles, masterDataLoaded } = useMasterData()
  const canApprove = canApproveMappings(roles)

  // Live refresh when the mapping store changes (save / delete / initial DB load).
  useEffect(() => {
    const refresh = () => setMappings(mappingStore.getAll())
    refresh()
    mappingStore.refresh().then(refresh)
    window.addEventListener(mappingStore.eventName, refresh)
    return () => window.removeEventListener(mappingStore.eventName, refresh)
  }, [])

  // ── Build one row per active generator, merged with its mapping groups ──
  const genRows: GenRow[] = (generators || [])
    .filter(g => g && g.status === 'active')
    .map(g => {
      const groups = mappings.filter(m => m && m.generatorId === g.id)
      const consumerCount = groups.reduce((s, m) => s + (m.consumers || []).length, 0)
      return { gen: g, groups, consumerCount, status: consumerCount > 0 ? 'active' : 'draft' }
    })

  const totalGenerators = genRows.length
  const draftCount      = genRows.filter(r => r.status === 'draft').length
  const activeCount     = genRows.filter(r => r.status === 'active').length
  const totalConsumers  = genRows.reduce((s, r) => s + r.consumerCount, 0)
  const pendingApprovalCount = mappings.filter(m => approvalOf(m) === 'pending').length

  const kpiCards = [
    {
      label: 'Total Generators',
      value: totalGenerators,
      icon: <ThunderboltOutlined style={{ fontSize: 24, color: 'var(--brand-primary)' }} />,
      iconBg: 'rgba(0,26,77,0.08)',
      borderColor: 'var(--brand-primary)',
    },
    {
      label: 'Draft',
      value: draftCount,
      icon: <WarningOutlined style={{ fontSize: 24, color: 'var(--brand-energy)' }} />,
      iconBg: 'rgba(245,158,11,0.08)',
      borderColor: 'var(--brand-energy)',
    },
    {
      label: 'Active',
      value: activeCount,
      icon: <CheckCircleOutlined style={{ fontSize: 24, color: 'var(--brand-green)' }} />,
      iconBg: 'rgba(5,150,105,0.08)',
      borderColor: 'var(--brand-green)',
    },
    {
      label: 'Pending Approval',
      value: pendingApprovalCount,
      icon: <SendOutlined style={{ fontSize: 24, color: '#2563EB' }} />,
      iconBg: 'rgba(37,99,235,0.08)',
      borderColor: '#2563EB',
    },
    {
      label: 'Total Consumers',
      value: totalConsumers,
      icon: <TeamOutlined style={{ fontSize: 24, color: 'var(--brand-primary)' }} />,
      iconBg: 'rgba(0,26,77,0.08)',
      borderColor: 'var(--brand-primary)',
    },
  ]

  // ── Build one row per active consumer, merged with every generator it's mapped to ──
  const consumerRows: ConsumerRow[] = (consumers || [])
    .filter(c => c && c.status === 'active')
    .map(c => {
      const links: ConsumerLink[] = mappings.flatMap(m =>
        (m.consumers || [])
          .filter(cr => cr && cr.consumerId === c.id)
          .map(cr => ({
            mappingId: m.id,
            generatorId: m.generatorId,
            generatorName: m.generatorName,
            plantName: m.plantName,
            htNumber: m.htNumber,
            shareHolding: cr.shareHolding,
            allocatedUnits: cr.allocatedUnits,
            tariff: cr.tariff,
            status: cr.status,
          }))
      )
      const totalAllocatedUnits = links.reduce((s, l) => s + Number(l.allocatedUnits || 0), 0)
      return { consumer: c, links, totalAllocatedUnits, status: links.length > 0 ? 'active' : 'draft' }
    })

  const filteredConsumers = consumerRows.filter(r =>
    (r.consumer?.name || '').toLowerCase().includes(consumerSearch.toLowerCase()) ||
    (r.consumer?.code || '').toLowerCase().includes(consumerSearch.toLowerCase()) ||
    (r.consumer?.id || '').toLowerCase().includes(consumerSearch.toLowerCase())
  )

  const consumerColumns = [
    {
      title: 'Consumer',
      key: 'consumer',
      render: (_: any, r: ConsumerRow) => (
        <div>
          <div style={{ fontWeight: 700, color: '#0B1426', fontSize: 14 }}>{r.consumer.name}</div>
          <div style={{ fontSize: 11, color: '#8A93A6', fontFamily: 'JetBrains Mono, monospace', marginTop: 2 }}>
            {r.consumer.code || r.consumer.id}
          </div>
        </div>
      ),
    },
    {
      title: 'Generators',
      key: 'generators',
      render: (_: any, r: ConsumerRow) => (
        <Badge
          count={new Set(r.links.map(l => l.generatorId)).size}
          showZero
          style={{ background: r.links.length ? '#001A4D' : '#CBD5E0', fontSize: 11, fontFamily: 'JetBrains Mono, monospace' }}
        />
      ),
    },
    {
      title: 'Allocation',
      key: 'allocation',
      render: (_: any, r: ConsumerRow) => {
        if (!r.links.length) return <span style={{ fontSize: 12, color: '#CBD5E0' }}>Not mapped yet</span>
        return (
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, maxWidth: 280 }}>
            {r.links.map((l, i) => (
              <Tooltip key={`${l.mappingId}-${i}`} title={`${l.generatorName}${l.plantName ? ` — ${l.plantName}` : ''} · ${l.shareHolding}% share`}>
                <span style={{ fontSize: 11, fontFamily: 'JetBrains Mono, monospace', fontWeight: 700, color: '#2563EB', background: '#2563EB14', borderRadius: 6, padding: '2px 8px' }}>
                  {l.generatorName}: {l.shareHolding}%
                </span>
              </Tooltip>
            ))}
          </div>
        )
      },
    },
    {
      title: 'Total Allocated Units',
      key: 'units',
      render: (_: any, r: ConsumerRow) => (
        r.totalAllocatedUnits
          ? (
            <Tooltip title={formatIndianFull(r.totalAllocatedUnits, { suffix: 'kWh/yr' })}>
              <span style={{ fontFamily: 'JetBrains Mono, monospace', fontWeight: 700 }}>
                {formatIndianCompact(r.totalAllocatedUnits, { suffix: 'kWh/yr' })}
              </span>
            </Tooltip>
          )
          : <span style={{ fontSize: 12, color: '#CBD5E0' }}>—</span>
      ),
    },
    {
      title: 'Status',
      key: 'status',
      render: (_: any, r: ConsumerRow) => (
        r.status === 'active'
          ? <span className="pill pill-green">Active</span>
          : <span className="pill pill-neutral">Draft</span>
      ),
    },
  ]

  // ── Consumer-tab expanded panel: which generator(s)/plant(s) supply this consumer ──
  const consumerExpandedRowRender = (r: ConsumerRow) => (
    <div style={{ padding: '14px 18px', background: '#F7F8FB' }}>
      {r.links.length === 0 ? (
        <div style={{ fontSize: 12, color: '#8A93A6' }}>This consumer isn't mapped to any generator yet.</div>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
          {r.links.map((l, i) => (
            <div
              key={`${l.mappingId}-${i}`}
              style={{ background: '#fff', border: '1px solid #E4E8F0', borderRadius: 10, padding: '12px 14px', display: 'flex', justifyContent: 'space-between', alignItems: 'center', cursor: 'pointer' }}
              onClick={() => navigate(`/mappings/${l.mappingId}`)}
            >
              <div>
                <span style={{ fontWeight: 700, fontSize: 13, color: '#1677ff' }}>{l.generatorName}</span>
                {l.plantName && <span style={{ fontSize: 11, color: '#8A93A6', marginLeft: 8 }}>{l.plantName}</span>}
                {l.htNumber && <span style={{ fontSize: 11, color: '#8A93A6', fontFamily: 'JetBrains Mono, monospace', marginLeft: 8 }}>{l.htNumber}</span>}
              </div>
              <Space size={16}>
                <span style={{ fontSize: 12, color: '#001A4D', fontFamily: 'JetBrains Mono, monospace', fontWeight: 700 }}>{l.shareHolding}%</span>
                <span style={{ fontSize: 12, color: '#0B1426', fontFamily: 'JetBrains Mono, monospace' }}>
                  {(l.allocatedUnits || 0).toLocaleString('en-IN')} kWh/yr
                </span>
                <span style={{ fontSize: 12, color: '#8A93A6' }}>₹{l.tariff}/kWh</span>
                <Tag color={l.status === 'active' ? 'green' : 'default'}>{l.status === 'active' ? 'Active' : 'Suspended'}</Tag>
              </Space>
            </div>
          ))}
        </div>
      )}
    </div>
  )

  const filtered = genRows.filter(r =>
    r.gen.name.toLowerCase().includes(search.toLowerCase()) ||
    (r.gen.code || '').toLowerCase().includes(search.toLowerCase()) ||
    r.gen.id.toLowerCase().includes(search.toLowerCase())
  )

  const openAdd = (genId: string, plantId?: string) => {
    setExpandedGenId(genId)
    setEditingGroupId(undefined)
    setSelectedUnmappedPlantId(plantId)
  }

  const deleteGroup = (id: string) => mappingStore.remove(id)

  // A generator still has room for "+ Add Consumers" only if at least one of
  // its plants is unmapped or under 100% allocated. Once every plant is at
  // (or over) 100%, there's nothing left to add — Edit an existing group
  // instead of opening a new one.
  const hasRemainingCapacity = (r: GenRow): boolean => {
    const genPlants = r.gen.plants || []
    if (!genPlants.length) return true // no plant catalog — can't determine capacity, don't block
    return genPlants.some(p => {
      const group = r.groups.find(g => g.plantId === p.id || (g.htNumber && p.htServiceNumber && g.htNumber === p.htServiceNumber))
      return !group || group.totalAllocated < 100
    })
  }

  // Generator-wide allocation %, weighted by each plant's proposed annual
  // generation (not just an average of the per-plant %s) — a small plant at
  // 100% shouldn't visually outweigh a large plant still sitting at 0%.
  const genAllocationSummary = (r: GenRow): { pct: number; totalCap: number } | null => {
    const genPlants = r.gen.plants || []
    if (!genPlants.length) return null
    let totalCap = 0
    let allocatedCap = 0
    genPlants.forEach(p => {
      const cap = Number(p.capacity || 0)
      totalCap += cap
      const group = r.groups.find(g => g.plantId === p.id || (g.htNumber && p.htServiceNumber && g.htNumber === p.htServiceNumber))
      if (group) allocatedCap += cap * (Math.min(Number(group.totalAllocated) || 0, 100) / 100)
    })
    if (totalCap <= 0) return null
    return { pct: Number(((allocatedCap / totalCap) * 100).toFixed(1)), totalCap }
  }

  const columns = [
    {
      title: 'Generator',
      key: 'generator',
      render: (_: any, r: GenRow) => (
        <div>
          <div style={{ fontWeight: 700, color: '#0B1426', fontSize: 14 }}>{r.gen.name}</div>
          <div style={{ fontSize: 11, color: '#8A93A6', fontFamily: 'JetBrains Mono, monospace', marginTop: 2 }}>
            {r.gen.code || r.gen.id}{r.gen.capacity ? ` · ${(r.gen.capacity / 1000).toFixed(1)} MWh/yr` : ''}
            {r.gen.plants?.length ? ` · ${r.gen.plants.length} plant${r.gen.plants.length === 1 ? '' : 's'}` : ''}
          </div>
        </div>
      ),
    },
    {
      title: 'Plant Type',
      key: 'plantType',
      render: (_: any, r: GenRow) => (
        <span className="pill" style={{ background: typeColor(r.gen.plantType) + '18', color: typeColor(r.gen.plantType) }}>
          {r.gen.plantType || '—'}
        </span>
      ),
    },
    {
      title: 'Consumers',
      key: 'consumers',
      render: (_: any, r: GenRow) => (
        <Badge
          count={r.consumerCount}
          showZero
          style={{ background: r.consumerCount ? '#001A4D' : '#CBD5E0', fontSize: 11, fontFamily: 'JetBrains Mono, monospace' }}
        />
      ),
    },
    {
      title: 'Allocation',
      key: 'allocation',
      render: (_: any, r: GenRow) => {
        const genPlants = r.gen.plants || []
        if (!genPlants.length && !r.groups.length) {
          return <span style={{ fontSize: 12, color: '#CBD5E0' }}>No plants</span>
        }

        const plantBadges: { id: string; name: string; allocatedText: string; color: string; tooltip: string; isUnmapped?: boolean }[] = []

        if (genPlants.length > 0) {
          genPlants.forEach(p => {
            const group = r.groups.find(g => g.plantId === p.id || (g.htNumber && p.htServiceNumber && g.htNumber === p.htServiceNumber))
            const pName = p.plantName || p.id
            if (group) {
              const color = group.totalAllocated === 100 ? '#059669' : group.totalAllocated > 100 ? '#DC2626' : '#F59E0B'
              plantBadges.push({
                id: p.id,
                name: pName,
                allocatedText: `${group.totalAllocated}%`,
                color,
                tooltip: `${pName} · ${group.totalAllocated}% allocated`,
              })
            } else {
              plantBadges.push({
                id: p.id,
                name: pName,
                allocatedText: 'Unmapped',
                color: '#D97706',
                isUnmapped: true,
                tooltip: `${pName} · Not mapped to any consumers yet`,
              })
            }
          })
        } else {
          r.groups.forEach(g => {
            const color = g.totalAllocated === 100 ? '#059669' : g.totalAllocated > 100 ? '#DC2626' : '#F59E0B'
            plantBadges.push({
              id: g.id,
              name: g.plantName || g.plantId || 'Plant',
              allocatedText: `${g.totalAllocated}%`,
              color,
              tooltip: `${g.plantName || g.plantId} · ${g.totalAllocated}% allocated`,
            })
          })
        }

        const summary = genAllocationSummary(r)

        return (
          <div style={{ maxWidth: 300 }}>
            {summary && (
              <div style={{ marginBottom: 6 }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 11, fontWeight: 600, color: '#4A5568', marginBottom: 2 }}>
                  <span>{summary.pct}% allocated</span>
                  <span style={{ color: '#8A93A6' }}>{Math.max(0, Number((100 - summary.pct).toFixed(1)))}% unallocated</span>
                </div>
                <Progress
                  percent={Math.min(summary.pct, 100)}
                  showInfo={false}
                  size="small"
                  strokeColor={summary.pct >= 100 ? '#059669' : summary.pct > 0 ? '#2563EB' : '#CBD5E0'}
                  trailColor="#E4E8F0"
                  style={{ margin: 0 }}
                />
              </div>
            )}
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
              {plantBadges.map(b => (
                <Tooltip key={b.id} title={b.tooltip}>
                  <span
                    style={{
                      fontSize: 11,
                      fontFamily: 'JetBrains Mono, monospace',
                      fontWeight: 700,
                      color: b.color,
                      background: b.color + (b.isUnmapped ? '18' : '14'),
                      borderRadius: 6,
                      padding: '2px 8px',
                      border: b.isUnmapped ? '1px dashed #FDE68A' : 'none',
                    }}
                  >
                    {b.name}: {b.allocatedText}
                  </span>
                </Tooltip>
              ))}
            </div>
          </div>
        )
      },
    },
    {
      title: 'Status',
      key: 'status',
      render: (_: any, r: GenRow) => (
        r.status === 'active'
          ? <span className="pill pill-green">Active</span>
          : <span className="pill pill-neutral">Draft</span>
      ),
    },
    {
      title: 'Actions',
      key: 'actions',
      render: (_: any, r: GenRow) => {
        const canAdd = hasRemainingCapacity(r)
        if (!canAdd) {
          return (
            <Tooltip title="Every plant for this generator is already 100% allocated — edit an existing group instead.">
              <Button
                type="default"
                size="small"
                disabled
                style={{ background: '#f5f5f5', color: '#8A93A6', borderColor: '#E4E8F0', fontWeight: 500 }}
              >
                Already Allocated
              </Button>
            </Tooltip>
          )
        }
        return (
          <Button
            type="primary"
            size="small"
            icon={<PlusOutlined />}
            onClick={(e) => { e.stopPropagation(); openAdd(r.gen.id) }}
          >
            Add Consumers
          </Button>
        )
      },
    },
  ]

  // ── Inline expanded panel: existing groups + unmapped plants + consumer editor ──
  const expandedRowRender = (r: GenRow) => {
    const allPlants = r.gen.plants || []
    const unmappedPlants = allPlants.filter(p => !r.groups.some(g => g.plantId === p.id || (g.htNumber && p.htServiceNumber && g.htNumber === p.htServiceNumber)))

    return (
      <div style={{ padding: '14px 18px', background: '#F7F8FB' }}>
        {/* Existing plant groups */}
        {r.groups.length > 0 && (
          <div style={{ marginBottom: 18 }}>
            <div style={{ fontSize: 11, fontWeight: 700, textTransform: 'uppercase', letterSpacing: 1, color: '#8A93A6', marginBottom: 10 }}>
              Existing Plant Groups ({r.groups.length})
            </div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
              {r.groups.map(g => (
                <div key={g.id} style={{ background: '#fff', border: '1px solid #E4E8F0', borderRadius: 10, padding: '12px 14px' }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 12, marginBottom: 10 }}>
                    <div>
                      <span style={{ fontWeight: 700, fontSize: 13, color: '#0B1426' }}>{g.plantName || g.plantId || 'Plant'}</span>
                      {g.htNumber && <span style={{ fontSize: 11, color: '#8A93A6', fontFamily: 'JetBrains Mono, monospace', marginLeft: 8 }}>{g.htNumber}</span>}
                      <span style={{ fontSize: 11, color: g.totalAllocated === 100 ? '#059669' : '#F59E0B', fontFamily: 'JetBrains Mono, monospace', fontWeight: 700, marginLeft: 8 }}>
                        {g.totalAllocated}% allocated
                      </span>
                    </div>
                    <Space size={4}>
                      <span className={`pill ${APPROVAL_PILL[approvalOf(g)]}`} style={{ fontSize: 10 }}>
                        {APPROVAL_LABEL[approvalOf(g)]}
                      </span>
                      {canSubmitForApproval(g) && (
                        <Tooltip title={approvalOf(g) === 'rejected' ? 'Resubmit for approval' : 'Submit for approval'}>
                          <Button size="small" icon={<SendOutlined />} onClick={() => submitForApproval(g.id)} />
                        </Tooltip>
                      )}
                      {approvalOf(g) === 'pending' && (canApprove ? (
                        <>
                          <Tooltip title="Approve mapping">
                            <Popconfirm title="Approve this mapping?" okText="Approve" cancelText="Cancel"
                              okButtonProps={{ style: { background: '#00A651', borderColor: '#00A651' } }}
                              onConfirm={() => approveMapping(g.id)}>
                              <Button size="small" icon={<CheckCircleOutlined />} style={{ color: '#059669', borderColor: '#059669' }} />
                            </Popconfirm>
                          </Tooltip>
                          <Tooltip title="Reject mapping">
                            <Button size="small" danger icon={<CloseCircleOutlined />}
                              onClick={() => { setRejectReason(''); setRejectTarget(g.id) }} />
                          </Tooltip>
                        </>
                      ) : (
                        <Tooltip title="Only an Admin can approve or reject a mapping">
                          <Tag color="default" style={{ fontSize: 10, margin: 0 }}>Awaiting admin</Tag>
                        </Tooltip>
                      ))}
                      <Button size="small" icon={<EyeOutlined />} onClick={() => navigate(`/mappings/${g.id}`)}>View</Button>
                      {canEdit && <Button size="small" icon={<EditOutlined />} onClick={() => { setExpandedGenId(r.gen.id); setEditingGroupId(g.id); setSelectedUnmappedPlantId(undefined); }}>Edit</Button>}
                      {canDelete && (
                        <Popconfirm title="Delete this plant group?" okText="Delete" cancelText="Cancel" okButtonProps={{ danger: true }} onConfirm={() => deleteGroup(g.id)}>
                          <Button size="small" danger icon={<DeleteOutlined />} />
                        </Popconfirm>
                      )}
                    </Space>
                  </div>
                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
                    {(g.consumers || []).map(c => (
                      <div key={c.consumerId} style={{ background: '#F7F8FB', border: '1px solid #E4E8F0', borderRadius: 8, padding: '6px 12px' }}>
                        <span style={{ fontWeight: 600, fontSize: 12.5, color: '#0B1426' }}>{c.consumerName}</span>
                        <span style={{ fontSize: 11, color: '#001A4D', fontFamily: 'JetBrains Mono, monospace', fontWeight: 700, marginLeft: 8 }}>{c.shareHolding}%</span>
                        <span style={{ fontSize: 11, color: '#8A93A6', marginLeft: 8 }}>₹{c.tariff}</span>
                      </div>
                    ))}
                  </div>
                </div>
              ))}
            </div>
            <Divider style={{ margin: '16px 0 12px' }} />
          </div>
        )}

        {/* Unmapped plants alert list */}
        {unmappedPlants.length > 0 && (
          <div style={{ marginBottom: 18 }}>
            <div style={{ fontSize: 11, fontWeight: 700, textTransform: 'uppercase', letterSpacing: 1, color: '#D97706', marginBottom: 10, display: 'flex', alignItems: 'center', gap: 6 }}>
              <WarningOutlined /> Unmapped Plants ({unmappedPlants.length}) — Action Needed
            </div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
              {unmappedPlants.map(p => (
                <div key={p.id} style={{ background: '#FFFBEB', border: '1px solid #FDE68A', borderRadius: 10, padding: '12px 14px', display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 16 }}>
                  <div>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                      <span style={{ fontWeight: 700, fontSize: 13, color: '#0B1426' }}>{p.plantName || p.id}</span>
                      {p.htServiceNumber && (
                        <span style={{ fontSize: 11, color: '#8A93A6', fontFamily: 'JetBrains Mono, monospace' }}>
                          HT: {p.htServiceNumber}
                        </span>
                      )}
                      <Tag color="warning" style={{ borderRadius: 4, fontWeight: 600, fontSize: 10 }}>Unmapped</Tag>
                    </div>
                    <div style={{ fontSize: 12, color: '#4A5568', marginTop: 4 }}>
                      Capacity: <strong>{Number(p.capacity || 0).toLocaleString('en-IN')} kWh/yr</strong> (~{formatUnitsKWh(annualToMonthlyUnits(p.capacity))}/mo)
                      {p.substationName && <span> · Substation: {p.substationName}</span>}
                      {p.plantAddress && <span> · Location: {p.plantAddress}</span>}
                    </div>
                  </div>
                  {canAdd && (
                    <Button
                      type="primary"
                      size="small"
                      icon={<PlusOutlined />}
                      style={{ background: '#D97706', borderColor: '#D97706' }}
                      onClick={() => openAdd(r.gen.id, p.id)}
                    >
                      Map Consumers to this Plant
                    </Button>
                  )}
                </div>
              ))}
            </div>
            <Divider style={{ margin: '16px 0 12px' }} />
          </div>
        )}

        {/* Editor */}
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 12 }}>
          <div style={{ fontSize: 11, fontWeight: 700, textTransform: 'uppercase', letterSpacing: 1, color: '#8A93A6' }}>
            {editingGroupId ? 'Edit Plant Group' : 'Add Consumers to a Plant'}
          </div>
          {editingGroupId && (
            <Button size="small" type="link" icon={<PlusOutlined />} onClick={() => setEditingGroupId(undefined)}>
              Add new plant group instead
            </Button>
          )}
        </div>
        <div style={{ background: '#fff', border: '1px solid #E4E8F0', borderRadius: 10, padding: 16 }}>
          <ConsumerAllocationEditor
            key={`${r.gen.id}-${editingGroupId ?? 'new'}-${selectedUnmappedPlantId ?? 'none'}`}
            generatorId={r.gen.id}
            mappingId={editingGroupId}
            initialPlantId={selectedUnmappedPlantId}
            compact
            onSaved={() => { setEditingGroupId(undefined); setSelectedUnmappedPlantId(undefined); setExpandedGenId(null); }}
            onCancel={() => { setEditingGroupId(undefined); setSelectedUnmappedPlantId(undefined); setExpandedGenId(null); }}
          />
        </div>
      </div>
    )
  }

  if (!mappingStore.loaded) return <TableSkeleton columns={6} />

  return (
    <div>
      {/* Page header */}
      <div style={{ marginBottom: 24 }}>
        <h2 className="page-title">Generator-Consumer Mapping</h2>
        <p className="page-subtitle">Every generator appears here automatically — use <b>+ Add Consumers</b> to allocate units to consumers</p>
      </div>

      {/* KPI Cards */}
      <KpiCardRow cards={kpiCards} colProps={{ xs: 24, sm: 12, lg: 8, xl: 4 }} />

      <WorkflowChainDisplay moduleName="Mapping" />

      {/* Generators / Consumers tabs */}
      <Tabs
        defaultActiveKey="generators"
        items={[
          {
            key: 'generators',
            label: (
              <span>
                <ThunderboltOutlined style={{ marginRight: 6 }} />
                Generators
              </span>
            ),
            children: (
              <Card
                bodyStyle={{ padding: 0 }}
                extra={
                  <Input
                    placeholder="Search generator..."
                    prefix={<SearchOutlined style={{ color: '#8A93A6' }} />}
                    style={{ width: 260 }}
                    value={search}
                    onChange={e => setSearch(e.target.value)}
                    allowClear
                  />
                }
                title={
                  <span style={{ fontWeight: 700, fontSize: 14, color: '#0B1426' }}>
                    <ThunderboltOutlined style={{ marginRight: 8, color: '#001A4D' }} />
                    Generators
                  </span>
                }
              >
                <Table
                  dataSource={filtered}
                  columns={columns}
                  rowKey={(r) => r.gen.id}
                  pagination={{ defaultPageSize: 10, pageSizeOptions: ['10', '25', '50', '100'], showSizeChanger: true, showTotal: (t) => `${t} generators` }}
                  expandable={{
                    expandedRowKeys: expandedGenId ? [expandedGenId] : [],
                    onExpand: (expanded, r) => {
                      setExpandedGenId(expanded ? r.gen.id : null)
                      setEditingGroupId(undefined)
                    },
                    expandedRowRender,
                  }}
                />
              </Card>
            ),
          },
          {
            key: 'consumers',
            label: (
              <span>
                <TeamOutlined style={{ marginRight: 6 }} />
                Consumers
              </span>
            ),
            children: (
              <Card
                bodyStyle={{ padding: 0 }}
                extra={
                  <Input
                    placeholder="Search consumer..."
                    prefix={<SearchOutlined style={{ color: '#8A93A6' }} />}
                    style={{ width: 260 }}
                    value={consumerSearch}
                    onChange={e => setConsumerSearch(e.target.value)}
                    allowClear
                  />
                }
                title={
                  <span style={{ fontWeight: 700, fontSize: 14, color: '#0B1426' }}>
                    <TeamOutlined style={{ marginRight: 8, color: '#001A4D' }} />
                    Consumers
                  </span>
                }
              >
                <Table
                  dataSource={filteredConsumers}
                  columns={consumerColumns}
                  rowKey={(r) => r.consumer.id}
                  pagination={{ defaultPageSize: 10, pageSizeOptions: ['10', '25', '50', '100'], showSizeChanger: true, showTotal: (t) => `${t} consumers` }}
                  expandable={{
                    expandedRowKeys: expandedConsumerId ? [expandedConsumerId] : [],
                    onExpand: (expanded, r) => setExpandedConsumerId(expanded ? r.consumer.id : null),
                    expandedRowRender: consumerExpandedRowRender,
                  }}
                />
              </Card>
            ),
          },
        ]}
      />

      {/* Legend */}
      <div style={{ marginTop: 16, display: 'flex', gap: 20, alignItems: 'center', padding: '0 4px' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 12, color: '#8A93A6' }}>
          <CheckCircleOutlined style={{ color: '#059669' }} /> Active — consumers allocated
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 12, color: '#8A93A6' }}>
          <WarningOutlined style={{ color: '#F59E0B' }} /> Draft — add consumers with the + button
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 12, color: '#8A93A6' }}>
          <SendOutlined style={{ color: '#2563EB' }} /> Each plant group is submitted for approval — only an Admin can approve or reject it
        </div>
      </div>

      {/* ── Reject Mapping Modal ──────────────────────────── */}
      <Modal
        title="Reject Mapping"
        open={!!rejectTarget}
        onCancel={() => setRejectTarget(null)}
        okText="Reject Mapping"
        okButtonProps={{ danger: true }}
        onOk={() => { if (rejectTarget && rejectMapping(rejectTarget, rejectReason)) setRejectTarget(null) }}
      >
        <p style={{ marginBottom: 8 }}>
          Why is this generator–consumer mapping being rejected? The reason is shown to whoever submitted it.
        </p>
        <Input.TextArea
          rows={3}
          value={rejectReason}
          onChange={e => setRejectReason(e.target.value)}
          placeholder="e.g. Allocation exceeds the consumer's sanctioned demand"
        />
      </Modal>
    </div>
  )
}

export default MappingList
